import { useCallback, useEffect, useMemo, useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import { Alert } from "../../components/Alert";
import { Modal } from "../../components/crm/Modal";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { StatusBadge } from "../../components/crm/StatusBadge";
import { Spinner } from "../../components/Spinner";
import {
  createDeal,
  isApiError,
  listCompanies,
  listContacts,
  listDeals,
  listPipelines,
  moveDealStage,
} from "../../lib/crm-api";
import type { Company, Contact, Deal, Pipeline, Stage } from "../../lib/crm-types";
import { formatMoney, fromMajorInput } from "../../lib/format";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

const PAGE_LIMIT = 100;

interface LostPrompt {
  deal: Deal;
  stage: Stage;
}

interface CreateForm {
  title: string;
  value: string;
  currency: string;
  companyId: string;
  contactId: string;
  stageId: string;
}

function emptyCreateForm(stageId: string): CreateForm {
  return { title: "", value: "", currency: "USD", companyId: "", contactId: "", stageId };
}

function sectionSum(deals: Deal[], currency: string, locale: string): string {
  let total = 0n;
  for (const deal of deals) {
    try {
      total += BigInt(deal.value_minor === "" ? "0" : deal.value_minor);
    } catch {
      // ignore malformed values
    }
  }
  return formatMoney(total.toString(), currency, locale);
}

function CreateDealDialog({
  pipeline,
  companies,
  contacts,
  onClose,
  onCreated,
}: {
  pipeline: Pipeline;
  companies: Company[];
  contacts: Contact[];
  onClose: () => void;
  onCreated: (deal: Deal) => void;
}): ReactNode {
  const { t } = useConfig();
  const stages = pipeline.stages ?? [];
  const [form, setForm] = useState<CreateForm>(emptyCreateForm(stages[0]?.id ?? ""));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (form.stageId === "") {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const deal = await createDeal({
        pipeline_id: pipeline.id,
        stage_id: form.stageId,
        title: form.title.trim(),
        value_minor: fromMajorInput(form.value),
        currency: form.currency.trim() === "" ? undefined : form.currency.trim().toUpperCase(),
        company_id: form.companyId === "" ? null : form.companyId,
        contact_id: form.contactId === "" ? null : form.contactId,
      });
      onCreated(deal);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={t("crm.deals.create")} onClose={onClose} busy={saving}>
      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      <form className="form" onSubmit={(event) => void onSubmit(event)}>
        <label className="field" htmlFor="deal-title">
          <span>{t("crm.deal.title")}</span>
          <input
            id="deal-title"
            required
            maxLength={200}
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
          />
        </label>
        <label className="field" htmlFor="deal-stage">
          <span>{t("crm.deal.stage")}</span>
          <select
            id="deal-stage"
            value={form.stageId}
            onChange={(event) => setForm((current) => ({ ...current, stageId: event.target.value }))}
          >
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field" htmlFor="deal-value">
          <span>{t("crm.deal.value")}</span>
          <input
            id="deal-value"
            inputMode="decimal"
            value={form.value}
            onChange={(event) => setForm((current) => ({ ...current, value: event.target.value }))}
          />
        </label>
        <label className="field" htmlFor="deal-currency">
          <span>{t("crm.deal.currency")}</span>
          <input
            id="deal-currency"
            maxLength={3}
            value={form.currency}
            onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value }))}
          />
        </label>
        <label className="field" htmlFor="deal-company">
          <span>{t("crm.deal.company")}</span>
          <select
            id="deal-company"
            value={form.companyId}
            onChange={(event) => setForm((current) => ({ ...current, companyId: event.target.value }))}
          >
            <option value="">{t("crm.common.none")}</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field" htmlFor="deal-contact">
          <span>{t("crm.deal.contact")}</span>
          <select
            id="deal-contact"
            value={form.contactId}
            onChange={(event) => setForm((current) => ({ ...current, contactId: event.target.value }))}
          >
            <option value="">{t("crm.common.none")}</option>
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {[contact.first_name, contact.last_name].filter(Boolean).join(" ")}
              </option>
            ))}
          </select>
        </label>
        <div className="form-actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving || form.stageId === ""}>
            {saving ? t("common.saving") : t("common.create")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function LostReasonDialog({
  onClose,
  onSubmit,
  busy,
  error,
}: {
  onClose: () => void;
  onSubmit: (reason: string) => void;
  busy: boolean;
  error: string | null;
}): ReactNode {
  const { t } = useConfig();
  const [reason, setReason] = useState("");
  return (
    <Modal title={t("crm.deal.lostReasonTitle")} onClose={onClose} busy={busy}>
      <p className="muted">{t("crm.deal.lostReasonHint")}</p>
      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          if (reason.trim() !== "") {
            onSubmit(reason.trim());
          }
        }}
      >
        <label className="field" htmlFor="lost-reason">
          <span>{t("crm.deal.lostReason")}</span>
          <input
            id="lost-reason"
            required
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <div className="form-actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn btn--danger" disabled={busy || reason.trim() === ""}>
            {busy ? t("common.saving") : t("common.confirm")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DealsInner(): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canCreate = hasPermission("crm.deal.create");
  const canMove = hasPermission("crm.deal.move");
  const canReadCompanies = hasPermission("crm.company.read");
  const canReadContacts = hasPermission("crm.contact.read");

  const [pipelines, setPipelines] = useState<Pipeline[] | null>(null);
  const [pipelineId, setPipelineId] = useState("");
  const [deals, setDeals] = useState<Deal[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [view, setView] = useState<"board" | "list">("board");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [lostPrompt, setLostPrompt] = useState<LostPrompt | null>(null);
  const [lostError, setLostError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const locale = lang === "es" ? "es-ES" : "en-US";

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await listPipelines();
        if (cancelled) return;
        setPipelines(page.items);
        const preferred =
          page.items.find((pipeline) => pipeline.is_default) ?? page.items[0] ?? null;
        if (preferred !== null) {
          setPipelineId(preferred.id);
        } else {
          setLoading(false);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (pipelineId === "") {
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await listDeals({ pipeline_id: pipelineId, limit: PAGE_LIMIT });
        if (!cancelled) {
          setDeals(page.items);
          setNextCursor(page.next_cursor);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pipelineId, t]);

  useEffect(() => {
    if (!canReadCompanies) return;
    let cancelled = false;
    void (async () => {
      try {
        const page = await listCompanies({ limit: PAGE_LIMIT });
        if (!cancelled) setCompanies(page.items);
      } catch {
        // optional
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canReadCompanies]);

  useEffect(() => {
    if (!canReadContacts) return;
    let cancelled = false;
    void (async () => {
      try {
        const page = await listContacts({ limit: PAGE_LIMIT });
        if (!cancelled) setContacts(page.items);
      } catch {
        // optional
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canReadContacts]);

  const pipeline = useMemo(
    () => pipelines?.find((row) => row.id === pipelineId) ?? null,
    [pipelines, pipelineId],
  );
  const stages = useMemo(
    () => [...(pipeline?.stages ?? [])].sort((a, b) => a.sort_order - b.sort_order),
    [pipeline],
  );

  const companyName = useCallback(
    (companyId: string | null): string => {
      if (companyId === null) return "—";
      return companies.find((company) => company.id === companyId)?.name ?? "—";
    },
    [companies],
  );
  const stageName = useCallback(
    (stageId: string): string => stages.find((stage) => stage.id === stageId)?.name ?? "—",
    [stages],
  );

  const applyDeal = useCallback((updated: Deal): void => {
    setDeals((current) => current.map((deal) => (deal.id === updated.id ? updated : deal)));
  }, []);

  const performMove = useCallback(
    async (deal: Deal, stage: Stage, lostReason?: string): Promise<void> => {
      setMovingId(deal.id);
      setError(null);
      try {
        const updated = await moveDealStage(deal.id, {
          stage_id: stage.id,
          ...(lostReason === undefined ? {} : { lost_reason: lostReason }),
        });
        applyDeal(updated);
        setLostPrompt(null);
      } catch (caught) {
        const message = isApiError(caught) ? caught.detail : t("error.network");
        if (lostPrompt !== null) {
          setLostError(message);
        } else {
          setError(message);
        }
      } finally {
        setMovingId(null);
      }
    },
    [applyDeal, lostPrompt, t],
  );

  const requestMove = useCallback(
    (deal: Deal, stage: Stage): void => {
      if (deal.stage_id === stage.id) {
        return;
      }
      if (stage.type === "lost" && deal.status !== "lost") {
        setLostError(null);
        setLostPrompt({ deal, stage });
        return;
      }
      void performMove(deal, stage);
    },
    [performMove],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>, stage: Stage): void => {
    event.preventDefault();
    if (dragId === null) return;
    const deal = deals.find((row) => row.id === dragId);
    setDragId(null);
    if (deal !== undefined) {
      requestMove(deal, stage);
    }
  };

  const onLoadMore = async (): Promise<void> => {
    if (nextCursor === null) return;
    setLoading(true);
    try {
      const page = await listDeals({
        pipeline_id: pipelineId,
        cursor: nextCursor,
        limit: PAGE_LIMIT,
      });
      setDeals((current) => [...current, ...page.items]);
      setNextCursor(page.next_cursor);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  };

  const boardCurrency = deals[0]?.currency ?? "USD";

  return (
    <section className="page page--wide" aria-labelledby="crm-deals-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="crm-deals-title">{t("crm.deals.title")}</h1>
          <p className="muted">{t("crm.deals.subtitle")}</p>
        </div>
        <div className="inline-actions">
          <div className="segmented" role="group" aria-label={t("crm.deals.view")}>
            <button
              type="button"
              className={`segmented__btn${view === "board" ? " is-active" : ""}`}
              aria-pressed={view === "board"}
              onClick={() => setView("board")}
            >
              {t("crm.deals.viewBoard")}
            </button>
            <button
              type="button"
              className={`segmented__btn${view === "list" ? " is-active" : ""}`}
              aria-pressed={view === "list"}
              onClick={() => setView("list")}
            >
              {t("crm.deals.viewList")}
            </button>
          </div>
          {canCreate && pipeline !== null ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setCreateOpen(true)}
            >
              {t("crm.deals.new")}
            </button>
          ) : null}
        </div>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      {pipelines !== null && pipelines.length > 1 ? (
        <label className="field filters__field-inline" htmlFor="deals-pipeline">
          <span>{t("crm.deal.pipeline")}</span>
          <select
            id="deals-pipeline"
            value={pipelineId}
            onChange={(event) => setPipelineId(event.target.value)}
          >
            {pipelines.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {loading && deals.length === 0 ? <Spinner label={t("app.loading")} /> : null}

      {!loading && pipeline !== null && deals.length === 0 ? (
        <p className="empty">{t("crm.deals.empty")}</p>
      ) : null}

      {view === "board" && pipeline !== null && stages.length > 0 ? (
        <div className="kanban" role="list">
          {stages.map((stage) => {
            const columnDeals = deals.filter((deal) => deal.stage_id === stage.id);
            return (
              <div
                className="kanban__column"
                key={stage.id}
                role="listitem"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => onDrop(event, stage)}
              >
                <header className="kanban__header">
                  <span className="kanban__name">
                    <span
                      className="kanban__dot"
                      style={stage.color === null ? undefined : { backgroundColor: stage.color }}
                      aria-hidden="true"
                    />
                    {stage.name}
                  </span>
                  <span className="kanban__count">{columnDeals.length}</span>
                </header>
                <p className="kanban__sum">
                  {sectionSum(columnDeals, boardCurrency, locale)}
                </p>
                <div className="kanban__cards">
                  {columnDeals.map((deal) => (
                    <article
                      key={deal.id}
                      className={`deal-card${dragId === deal.id ? " is-dragging" : ""}`}
                      draggable={canMove}
                      onDragStart={() => setDragId(deal.id)}
                      onDragEnd={() => setDragId(null)}
                    >
                      <p className="deal-card__title">{deal.title}</p>
                      <p className="deal-card__value">
                        {formatMoney(deal.value_minor, deal.currency, locale)}
                      </p>
                      <p className="muted deal-card__company">{companyName(deal.company_id)}</p>
                      <div className="deal-card__meta">
                        <StatusBadge status={deal.status} />
                        {deal.probability !== null ? (
                          <span className="muted">{deal.probability}%</span>
                        ) : null}
                      </div>
                      {canMove ? (
                        <label className="deal-card__move">
                          <span className="sr-only">{t("crm.deal.moveTo")}</span>
                          <select
                            value={deal.stage_id}
                            disabled={movingId === deal.id}
                            onChange={(event) => {
                              const target = stages.find(
                                (row) => row.id === event.target.value,
                              );
                              if (target !== undefined) {
                                requestMove(deal, target);
                              }
                            }}
                          >
                            {stages.map((row) => (
                              <option key={row.id} value={row.id}>
                                {row.name}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {view === "list" && deals.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("crm.deals.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("crm.deal.title")}</th>
                <th scope="col">{t("crm.deal.company")}</th>
                <th scope="col">{t("crm.deal.stage")}</th>
                <th scope="col">{t("crm.deal.value")}</th>
                <th scope="col">{t("crm.common.status")}</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((deal) => (
                <tr key={deal.id}>
                  <td>{deal.title}</td>
                  <td>{companyName(deal.company_id)}</td>
                  <td>{stageName(deal.stage_id)}</td>
                  <td>{formatMoney(deal.value_minor, deal.currency, locale)}</td>
                  <td>
                    <StatusBadge status={deal.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {nextCursor !== null ? (
        <div className="form-actions">
          <button
            type="button"
            className="btn btn--ghost"
            disabled={loading}
            onClick={() => void onLoadMore()}
          >
            {t("crm.common.loadMore")}
          </button>
        </div>
      ) : null}

      {createOpen && pipeline !== null ? (
        <CreateDealDialog
          pipeline={pipeline}
          companies={companies}
          contacts={contacts}
          onClose={() => setCreateOpen(false)}
          onCreated={(deal) => {
            setDeals((current) => [deal, ...current]);
            setCreateOpen(false);
          }}
        />
      ) : null}

      {lostPrompt !== null ? (
        <LostReasonDialog
          onClose={() => {
            setLostPrompt(null);
            setLostError(null);
          }}
          busy={movingId === lostPrompt.deal.id}
          error={lostError}
          onSubmit={(reason) => void performMove(lostPrompt.deal, lostPrompt.stage, reason)}
        />
      ) : null}
    </section>
  );
}

export function DealsPage(): ReactNode {
  return (
    <RequirePermission permission="crm.deal.read">
      <DealsInner />
    </RequirePermission>
  );
}
